import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { useModalStyles } from "@/pages/laboratory/styles/modalStyles";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Alert,
	Button,
	CircularProgress,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { SyntheticEvent, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	CATALOG_SECTION_INITIAL_VALUES,
	CatalogSection,
} from "../../../models/CatalogSection";
import {
	getCatalogSection,
	upsertCatalogSection,
} from "../../../repositories/CatalogSectionRepository";

type Props = {
	SectionId: number | null;
	show: boolean;
	onHide: (shouldUpdate?: boolean) => void;
};

export const EditSection = ({ SectionId, show, onHide }: Props) => {
	const classes = useModalStyles();
	const [open, setOpen] = useState("");
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const {
		register,
		control,
		setValue,
		handleSubmit,
		clearErrors,
		formState: { errors },
	} = useForm<CatalogSection>({
		defaultValues: CATALOG_SECTION_INITIAL_VALUES,
	});
	const handleClose = (
		event: Event | SyntheticEvent<any, Event>,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen("");
	};
	const onSubmit: SubmitHandler<CatalogSection> = async (data) => {
		setIsSubmitLoading(true);
		const res = await upsertCatalogSection(data);
		if (res.ok) {
			const id = res.data.tables[0].rows[0].Id;
			onHide(true);
			setOpen("success");
		} else {
			setError(res.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			clearErrors();
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const response = await getCatalogSection(SectionId);
				if (response.ok) {
					const sectionSelected = response.data.tables[0].rows[0];
					if (sectionSelected) {
						setValue("SectionId", sectionSelected.SectionId);
						setValue("SectionName", sectionSelected.SectionName);
						setValue("IsCircular", sectionSelected.IsCircular);
						setValue("IsActive", sectionSelected.IsActive);
						setIsLoading(false);
					}
				} else {
					setError(response.message);
				}
			})();
		}
	}, [show]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				aria-labelledby="contained-modal-title-vcenter"
				title="EDIT SECTION"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<CircularProgress />
							</Grid>
						)}
						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Grid size={{ xs: 12, md: 12 }}>
										<TextField
											{...register("SectionName", { required: true })}
											label="Name"
											variant="outlined"
											error={!!errors.SectionName}
											helperText={errors.SectionName?.message}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 6 }}>
										<Controller
											name="IsCircular"
											control={control}
											render={({ field }) => (
												<TextFieldWithCheckbox
													checkboxValue={field.value}
													onCheckboxChange={field.onChange}
													label="Is Circular"
													className={classes.checkbox}
												/>
											)}
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 6 }}>
										<Controller
											name="IsActive"
											control={control}
											render={({ field }) => (
												<TextFieldWithCheckbox
													checkboxValue={field.value}
													onCheckboxChange={field.onChange}
													label="Enabled"
													className={classes.checkbox}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={open === "success"}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The section was updated successfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
